import { Navigate, Route, Routes } from 'react-router-dom'
import { useAuth } from './lib/auth/AuthContext'
import { LoginPage } from './routes/login'
import { SignupPage } from './routes/signup'
import { DashboardPage } from './routes/dashboard'
import { RenterListPage } from './pages/renters/RenterListPage'
import { RenterCreatePage } from './pages/renters/RenterCreatePage'
import { RenterDetailPage } from './pages/renters/RenterDetailPage'
import { LeaseListPage } from './pages/leases/LeaseListPage'
import { LeaseCreatePage } from './pages/leases/LeaseCreatePage'
import { LeaseDetailPage } from './pages/leases/LeaseDetailPage'
import { ContractListPage } from './pages/contracts/ContractListPage'
import { ContractWizardPage } from './pages/contracts/ContractWizardPage'
import { LejeforholdListPage } from './pages/contracts/LejeforholdListPage'
import { LejeforholdDetailPage } from './pages/contracts/LejeforholdDetailPage'
import { PropertyListPage } from './pages/properties/PropertyListPage'
import { PropertyCreatePage } from './pages/properties/PropertyCreatePage'
import { PropertyDetailPage } from './pages/properties/PropertyDetailPage'
import { CompanyDetailPage } from './pages/companies/CompanyDetailPage'
import { FinancialListPage } from './pages/financial/FinancialListPage'
import { KontoplanPage } from './pages/financial/KontoplanPage'
import { PosteringerPage } from './pages/financial/PosteringerPage'
import { ForbrugsregnskabPage } from './pages/financial/ForbrugsregnskabPage'
import { FraflytningsopgoerelsePage } from './pages/financial/FraflytningsopgoerelsePage'
import { RestancePage } from './pages/arrears/RestancePage'
import { KommunikationPage } from './pages/communications/KommunikationPage'
import { ListerPage } from './pages/exports/ListerPage'
import { InspectionListPage } from './pages/inspections/InspectionListPage'
import { InspectionWizardPage } from './pages/inspections/InspectionWizardPage'
import { HenvendelserPage } from './pages/henvendelser/HenvendelserPage'
import { LejerAISettingsPage } from './pages/settings/LejerAISettingsPage'
import { ListingsPage } from './pages/listings/ListingsPage'
import { ListingDetailPage } from './pages/listings/ListingDetailPage'
import './index.css'

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth()
  if (isLoading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: '#94a3b8', fontSize: 13 }}>Henter…</p>
      </div>
    )
  }
  if (!user) {
    return <Navigate to="/login" replace />
  }
  return <>{children}</>
}

function Protected({ children }: { children: React.ReactNode }) {
  return <ProtectedRoute>{children}</ProtectedRoute>
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />

      <Route path="/dashboard" element={<Protected><DashboardPage /></Protected>} />

      {/* Virksomhed */}
      <Route path="/virksomhed" element={<Protected><CompanyDetailPage /></Protected>} />
      <Route path="/virksomheder" element={<Navigate to="/virksomhed" replace />} />
      <Route path="/virksomheder/:id" element={<Navigate to="/virksomhed" replace />} />

      {/* Ejendomme */}
      <Route path="/ejendomme" element={<Protected><PropertyListPage /></Protected>} />
      <Route path="/ejendomme/opret" element={<Protected><PropertyCreatePage /></Protected>} />
      <Route path="/ejendomme/:id" element={<Protected><PropertyDetailPage /></Protected>} />

      {/* Lejemål */}
      <Route path="/lejemaal" element={<Protected><LeaseListPage /></Protected>} />
      <Route path="/lejemaal/opret" element={<Protected><LeaseCreatePage /></Protected>} />
      <Route path="/lejemaal/:id" element={<Protected><LeaseDetailPage /></Protected>} />

      {/* Annoncering */}
      <Route path="/annoncering" element={<Protected><ListingsPage /></Protected>} />
      <Route path="/annoncering/:id" element={<Protected><ListingDetailPage /></Protected>} />

      {/* Lejeforhold (contracts — UI rename only) */}
      <Route path="/lejeforhold" element={<Protected><LejeforholdListPage /></Protected>} />
      <Route path="/lejeforhold/:id" element={<Protected><LejeforholdDetailPage /></Protected>} />
      <Route path="/kontrakter" element={<Navigate to="/lejeforhold" replace />} />
      <Route path="/kontrakter/opret" element={<Protected><ContractWizardPage /></Protected>} />
      <Route path="/kontrakter/:id" element={<Protected><ContractListPage /></Protected>} />

      {/* Lejere */}
      <Route path="/lejere" element={<Protected><RenterListPage /></Protected>} />
      <Route path="/lejere/opret" element={<Protected><RenterCreatePage /></Protected>} />
      <Route path="/lejere/:id" element={<Protected><RenterDetailPage /></Protected>} />
      <Route path="/henvendelser" element={<Protected><HenvendelserPage /></Protected>} />
      <Route path="/indstillinger/lejer-ai" element={<Protected><LejerAISettingsPage /></Protected>} />

      {/* Flyttesyn */}
      <Route path="/flyttesyn" element={<Protected><InspectionListPage /></Protected>} />
      <Route path="/flyttesyn/:id" element={<Protected><InspectionWizardPage /></Protected>} />

      {/* Økonomi, Restance, Kommunikation, Lister */}
      <Route path="/okonomi" element={<Protected><FinancialListPage /></Protected>} />
      <Route path="/kontoplan" element={<Protected><KontoplanPage /></Protected>} />
      <Route path="/posteringer" element={<Protected><PosteringerPage /></Protected>} />
      <Route path="/forbrugsregnskab" element={<Protected><ForbrugsregnskabPage /></Protected>} />
      <Route path="/fraflytningsopgoerelser" element={<Protected><FraflytningsopgoerelsePage /></Protected>} />
      <Route path="/restance" element={<Protected><RestancePage /></Protected>} />
      <Route path="/kommunikation" element={<Protected><KommunikationPage /></Protected>} />
      <Route path="/lister" element={<Protected><ListerPage /></Protected>} />

      {/* Lejer portal is now the standalone tenant-portal app (token endpoint
          returns its URL). The embedded /lejer/:token duplicate is retired. */}

      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  )
}
